import "./DeleteConfirmationDialog.scss";

import React from "react";
import { createPortal } from "react-dom";
import { Modal, ModalHeader, ModalContent } from "@metro-ui/core";
import { FormattedMessage } from "react-intl";

import { ImageWithFallbacks } from "./ImageWithFallbacks";
import { getUrls } from "../cart-page/ArticleBadgeImage";
import { UiTrackedButton } from "./TrackedComponents";

import type { ImageSources } from "../../model/ArticleTypes";

export type DeleteConfirmationDialogProps = {
	show: boolean;
	onCancel: () => void;
	onDelete: () => void;
	items?: DeleteItemView[] | null;
};
export type DeleteItemView = {
	bundleId: string;
	description: string | null | undefined;
	imageSources: ImageSources;
};
type ExtendedDeleteConfirmationDialogProps = {
	titleKey: string;
	bodyKey: string;
	cancelBtnKey: string;
	confirmBtnKey: string;
} & DeleteConfirmationDialogProps;

export class DeleteConfirmationDialog extends React.Component<ExtendedDeleteConfirmationDialogProps> {
	_handleDialogKeyDown(event: React.KeyboardEvent): void {
		switch (event.key) {
			case "Enter":
				this.props.onDelete();
				break;
			case "Escape":
				this.props.onCancel();
				break;
			default:
				return;
		}

		event.preventDefault();
	}

	_renderItems(items: DeleteItemView[]) {
		if (items.length === 0) {
			return null;
		}

		return items.length > 1 ? (
			<ul>
				{items.map((itemData) => (
					<li key={itemData.bundleId}>
						<div className="text-overflow-ellipsis">{itemData.description ?? itemData.bundleId}</div>
					</li>
				))}
			</ul>
		) : (
			items[0] && (
				<div className="article-image">
					<ImageWithFallbacks urls={getUrls(items[0])} />
				</div>
			)
		);
	}

	render() {
		const items: DeleteItemView[] = this.props.items || [];
		const articleDescription: string | null | undefined =
			items.length === 1 ? (items[0].description ?? items[0].bundleId) : null;
		return createPortal(
			<Modal
				isOpen={this.props.show}
				data-testid="test-delete-modal"
				onDismiss={(event) => {
					if (event.type === "keydown" && (event as KeyboardEvent).key === "Enter") {
						return;
					}
					this.props.onCancel();
				}}
				onKeyDown={this._handleDialogKeyDown.bind(this)}
				closeOnOutsideClick
				zIndex={1050}
				overlayVariant="dark"
			>
				<ModalHeader
					className="deletedialog-header"
					align="end"
					padding="medium"
					spacing="medium"
					isClosable
				></ModalHeader>
				<ModalContent className="deletedialog-content" direction="column" padding="medium" gap="small">
					<div>
						<h2 className="deletedialog-content-title">
							<FormattedMessage id={this.props.titleKey} />
						</h2>
						<p className="deletedialog-content-description">
							<FormattedMessage
								id={this.props.bodyKey}
								values={{
									articleDescription,
								}}
							/>
						</p>
						<p className="deletedialog-images-wrapper">{this._renderItems(items)}</p>
						<div className="deletedialog-buttons">
							<UiTrackedButton
								tags={{
									source: "DeleteCartConfirmationDialog.confirm",
								}}
								className="test-delete-confirm"
								autoFocus
								data-grow
								data-variant="action"
								onClick={this.props.onDelete}
							>
								<FormattedMessage id={this.props.confirmBtnKey} />
							</UiTrackedButton>
							<UiTrackedButton
								tags={{
									source: "DeleteCartConfirmationDialog.cancel",
								}}
								className="test-delete-cancel"
								data-grow
								onClick={this.props.onCancel}
							>
								<FormattedMessage id={this.props.cancelBtnKey} />
							</UiTrackedButton>
						</div>
					</div>
				</ModalContent>
			</Modal>,
			document.body,
		);
	}
}
export const DeleteCartConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_SHOPPINGCARTPAGE_DELETE_CART_QUESTION",
		bodyKey: "ORC_SHOPPINGCARTPAGE_DELETE_CART_QUESTION_SUBTITLE",
		cancelBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_CANCEL",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};
export const DeleteItemConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_SHOPPINGCARTPAGE_DELETE_ITEM_QUESTION_SHORT",
		bodyKey: "ORC_SHOPPINGCARTPAGE_DELETE_ITEM_QUESTION_SUBTITLE_WITH_DESCRIPTION",
		cancelBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_CANCEL",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};
export const DeleteOrderConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_FAILED_SUBMIT_DELETE_QUESTION",
		bodyKey: "ORC_FAILED_SUBMIT_DELETE_QUESTION_SUBTITLE",
		cancelBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_CANCEL",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};

export const DeleteItemsOneClickConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_SHOPPINGCARTPAGE_MODAL_SUPPLIER_MOV_TITLE",
		bodyKey: "ORC_SHOPPINGCARTPAGE_MODAL_SUPPLIER_MOV_ONE_CLICK_TEXT",
		cancelBtnKey: "ORC_CHECKOUTNAVIGATION_BACK",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES_ONE_CLICK",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};

export const DeleteItemsCheckoutConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_SHOPPINGCARTPAGE_MODAL_SUPPLIER_MOV_TITLE",
		bodyKey: "ORC_SHOPPINGCARTPAGE_MODAL_SUPPLIER_MOV_CHECKOUT_TEXT",
		cancelBtnKey: "ORC_CHECKOUTNAVIGATION_BACK",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES_CHECKOUT",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};

export const DeleteSavedCartConfirmationDialog = (props: DeleteConfirmationDialogProps) => {
	const extendedProps = {
		...props,
		titleKey: "ORC_SHOPPINGCARTPAGE_DELETE_CART_QUESTION",
		bodyKey: "ORC_SHOPPINGCARTPAGE_DELETE_CART_QUESTION_SUBTITLE",
		cancelBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_CANCEL",
		confirmBtnKey: "ORC_SHOPPINGCARTPAGE_MODAL_BUTTON_YES",
	};
	return <DeleteConfirmationDialog {...extendedProps} />;
};
